import { countries as allCountryCodes } from 'country-flag-icons';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

// Register English locale
countries.registerLocale(enLocale);

// Popular FPL countries (shown first in the list)
const POPULAR_FPL_COUNTRIES = [
  'GB', 'US', 'IN', 'NG', 'EG', 'AU', 'CA', 'IE', 'ZA', 'KE',
  'PH', 'MY', 'SG', 'AE', 'SA', 'PK', 'BD', 'GH', 'NO', 'SE',
  'DK', 'FI', 'DE', 'FR', 'ES', 'IT', 'PT', 'NL', 'BE', 'PL',
];

export interface CountryOption {
  code: string;      // ISO 3166-1 alpha-2
  name: string;      // Full name
  flag: string;      // Emoji flag
}

/**
 * Get emoji flag by country code
 */
export function getCountryFlag(code: string): string {
  return getUnicodeFlagIcon(code) || code;
}

/**
 * Get country name by code
 */
export function getCountryName(code: string): string {
  return countries.getName(code, 'en') || code;
}

/**
 * Get sorted list of countries (popular FPL countries first)
 */
export function getCountryOptions(): CountryOption[] {
  // Filter to only valid country codes that have names
  const validCodes = allCountryCodes.filter(code => countries.getName(code, 'en'));

  // Sort: popular countries first, then alphabetically by name
  const sorted = validCodes.sort((a, b) => {
    const aPopular = POPULAR_FPL_COUNTRIES.indexOf(a);
    const bPopular = POPULAR_FPL_COUNTRIES.indexOf(b);

    // Both are popular - sort by order in POPULAR_FPL_COUNTRIES
    if (aPopular !== -1 && bPopular !== -1) return aPopular - bPopular;
    // Only a is popular
    if (aPopular !== -1) return -1;
    // Only b is popular
    if (bPopular !== -1) return 1;

    // Neither is popular - sort alphabetically by name
    return getCountryName(a).localeCompare(getCountryName(b));
  });

  return sorted.map(code => ({
    code,
    name: getCountryName(code),
    flag: getCountryFlag(code),
  }));
}

/**
 * Search countries by name or code
 */
export function searchCountries(query: string): CountryOption[] {
  if (!query.trim()) return getCountryOptions();

  const lowerQuery = query.toLowerCase().trim();
  return getCountryOptions().filter(c =>
    c.name.toLowerCase().includes(lowerQuery) ||
    c.code.toLowerCase().includes(lowerQuery),
  );
}

/**
 * Check if a country code is valid
 */
export function isValidCountryCode(code: string): boolean {
  return /^[A-Z]{2}$/.test(code) && !!countries.getName(code, 'en');
}
